// The map's list, made easy to find things in with hundreds of places (features/map.md): a search that
// ignores capitals, accents and hyphens, and groups by where you stay. Plain functions with type-only
// imports, so the server-side tests can use them too.
import { dayNumber, daySpan } from "../../src/days.ts";
import type { Item } from "../../src/web/types.ts";
import { daysSpanned, KINDS, type Kind, kindOf } from "./format.ts";
import { bounds, distanceM, type LngLat, midway, point } from "./geo.ts";

// A place goes under the stay it's nearest to within this, as the crow flies; else under Elsewhere.
export const NEAR_STAY_M = 30_000;
// Past this many places, every group but the first starts folded.
export const FOLD_PAST = 30;
// How long typing pauses before the map brings the search's matches into view.
export const SEARCH_PAUSE_MS = 600;

// ---------- Search ----------

// Letters with no accent to take off, as people type them without one.
const PLAIN: Record<string, string> = { ø: "o", ł: "l", đ: "d", ð: "d", æ: "ae", œ: "oe", ß: "ss", þ: "th", ı: "i" };

// Text as search compares it: no capitals, no accents (Sensō-ji is "sensoji", Søren "soren"), and no
// hyphens or apostrophes, so "kiyomizudera" finds Kiyomizu-dera and "philosophers" Philosopher's Path.
export function folded(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[øłđðæœßþı]/g, (c) => PLAIN[c] ?? c)
    .replace(/[-‐-―'‘’ʼ.]/g, "");
}

// What a search looks in for an item: its name, its category, where it is (an area's name, an address,
// its city, a journey's ends) and its notes, folded once.
export function searchText(item: Item): string {
  return folded(
    [
      item.title,
      item.category,
      item.where?.name,
      item.where?.address,
      item.where?.city,
      item.from?.name,
      item.from?.address,
      item.to?.name,
      item.to?.address,
      item.notes,
    ]
      .filter(Boolean)
      .join("\n"),
  );
}

// The words of a search, each to be found: "temple kyoto" finds a temple whose address says Kyoto.
export function searchWords(query: string): string[] {
  return query
    .split(/\s+/)
    .map(folded)
    .filter((w) => w.length > 0);
}

// The ids of what a search finds among `texts` (id to searchText), or null with nothing to search for.
export function found(texts: ReadonlyMap<string, string>, query: string): Set<string> | null {
  const words = searchWords(query);
  if (words.length === 0) return null;
  const out = new Set<string>();
  for (const [id, text] of texts) if (words.every((w) => text.includes(w))) out.add(id);
  return out;
}

// ---------- Groups ----------

export interface GroupHead {
  // Kept with what's folded: "city:<city>" (folded), "near:<stay>" for a stay with no city, "elsewhere",
  // or "kind:<kind>".
  key: string;
  // The stays' city ("Osaka"), "Near <stay>" for a stay with no city, "Elsewhere", or a kind.
  label: string;
  // Its stays' nights: "5 to 9 Dec · 4 nights".
  caption: string | null;
  // Where we stay that heads it, in the order we stay there: none for Elsewhere and the kinds.
  stays: Item[];
}

export interface ListLayout {
  // Grouped by where we stay (else by kind, while no chosen or booked stay has a place yet).
  byStay: boolean;
  heads: GroupHead[];
  // Every item on the map in list order, with its group's index in `heads`, and for a choice's open
  // option, its choice (a choice's options are next to each other).
  order: { item: Item; group: number; choice?: Item }[];
}

// A row of a group, or a choice's open options together under its title.
export type ListEntry = { item: Item } | { choice: Item; options: Item[] };

export interface ListGroup extends GroupHead {
  // Everything shown in it, options too (how many it has).
  items: Item[];
  entries: ListEntry[];
}

// A stay counts as where we stay once it's chosen or booked (or stayed at).
const SETTLED = new Set(["planned", "booked", "done"]);
const collator = new Intl.Collator("en", { sensitivity: "base", numeric: true });
const byName = (a: Item, b: Item) => collator.compare(a.title, b.title);

// Within a group: the stay first, then the other stays, places by kind, places that are a station or an
// airport, then journeys.
const PLACE_ORDER: Kind[] = ["stays", "food", "sights", "nature", "shopping", "other", "around"];

const firstDay = (item: Item, start: string | null) => daySpan(item.when, start)?.first ?? Number.POSITIVE_INFINITY;
const startsAt = (item: Item) =>
  item.when && "start" in item.when ? item.when.start : item.when?.precision === "day" ? item.when.day : "";

// Where a journey leaves from: its start when it has a place, else wherever it's drawn.
function leaves(item: Item): LngLat | null {
  if (item.type === "move" && item.from?.lat !== undefined && item.from.lng !== undefined)
    return [item.from.lng, item.from.lat];
  return point(item);
}

// The nights at a stay, as its group's caption: "5 to 9 Dec · 4 nights" (or the days alone).
export function stayNights(stay: Item, start: string | null): string | null {
  return staysNights([stay], start);
}

// The nights at a group's stays, in the order we stay there, as its caption: one stay's as stayNights
// says them; stays back to back (or overlapping) as one span with their nights together, "25 Dec to 1 Jan
// · 7 nights"; a city stayed in twice, both spans and all their nights, "1 to 5 Dec and 28 to 30 Dec · 6
// nights". Stays with no days yet add nothing; none with days, no caption.
export function staysNights(stays: readonly Item[], start: string | null): string | null {
  type Span = { first: string; last: string; a: number | null; b: number | null };
  const spans: Span[] = [];
  for (const { when } of stays) {
    // Days, or check-in to check-out with their times (features/stays-and-moves.md): the same nights.
    const [first, last] =
      when?.precision === "days"
        ? [when.start, when.end]
        : when?.precision === "day"
          ? [when.day, when.day]
          : (when?.precision === "exact" || when?.precision === "window") && when.end
            ? [when.start.slice(0, 10), when.end.slice(0, 10)]
            : [];
    if (first === undefined || last === undefined) continue;
    const next: Span = { first, last, a: dayNumber(first, start), b: dayNumber(last, start) };
    const prev = spans.at(-1);
    // Checking in where the last one checks out (or before): the same span.
    if (prev && prev.b !== null && next.a !== null && next.b !== null && next.a <= prev.b) {
      if (next.b > prev.b) Object.assign(prev, { last: next.last, b: next.b });
    } else spans.push(next);
  }
  if (spans.length === 0) return null;
  const nights = spans.map(({ a, b }) => (a !== null && b !== null && b > a ? b - a : null));
  const days = listed(spans.map((s) => daysSpanned(s)));
  if (nights.some((n) => n === null)) return days;
  const n = nights.reduce<number>((sum, k) => sum + (k ?? 0), 0);
  return `${days} · ${n} ${n === 1 ? "night" : "nights"}`;
}

// "A", "A and B", "A, B and C".
const listed = (parts: string[]) =>
  parts.length <= 1 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;

// A stay's city, as written, when it has one.
const cityOf = (stay: Item): string | null => stay.where?.city?.trim() || null;

// How the list of what's on the map is laid out, from everything on it (whatever the filters show, so
// groups stay put as filters and searches come and go): under where we stay, each group headed by its
// stays' city ("Osaka"), the stays in one city one group, where its first stay is, and a stay with no
// city a group of its own, "Near <stay>"; then Elsewhere. `start` is the trip's first date; `optionsOf`,
// each choice's open options on the map, with its choice (plan.ts openOptions): they go together, right
// after the group's stay, and never head a group. When the choice has one chosen or booked that's
// somewhere we stay, they go under it: they're its alternatives, wherever each is (a hotel 28 km out
// still sits with the one it would replace). Otherwise to the group of the stay nearest their middle (in a
// trip grouped by kind, under their kind).
export function listLayout(
  placed: Item[],
  start: string | null,
  optionsOf: ReadonlyMap<string, Item> = new Map(),
): ListLayout {
  const stays = placed
    .filter((i) => i.type === "stay" && SETTLED.has(i.status) && !optionsOf.has(i.id))
    .sort((a, b) => firstDay(a, start) - firstDay(b, start) || startsAt(a).localeCompare(startsAt(b)) || byName(a, b));
  const byStay = stays.length > 0;
  // Each stay's group, by its place in `stays`.
  const groupOf: number[] = [];
  const heads: GroupHead[] = [];
  if (byStay) {
    const byKey = new Map<string, GroupHead>();
    for (const s of stays) {
      const city = cityOf(s);
      const key = city ? `city:${folded(city.replace(/\s+/g, " "))}` : `near:${s.id}`;
      let head = byKey.get(key);
      if (!head) {
        head = { key, label: city ?? `Near ${s.title}`, caption: null, stays: [] };
        byKey.set(key, head);
        heads.push(head);
      }
      head.stays.push(s);
      groupOf.push(heads.indexOf(head));
    }
    for (const head of heads) head.caption = staysNights(head.stays, start);
    heads.push({ key: "elsewhere", label: "Elsewhere", caption: null, stays: [] });
  } else {
    // No stay with a place yet: by kind, in the What filter's order.
    heads.push(...KINDS.map((k) => ({ key: `kind:${k.key}`, label: k.label, caption: null, stays: [] })));
  }
  const elsewhere = heads.length - 1;
  const kindIndex = new Map(KINDS.map((k, n) => [k.key as Kind, n]));
  const own = new Map(stays.map((s, n) => [s.id, n]));
  const at = stays.map((s) => point(s) as LngLat);
  // The group for something at `from`: the nearest stay's within NEAR_STAY_M (the first in stay order
  // when two are as near), else Elsewhere; by kind, its kind's.
  const groupAt = (from: LngLat | null, item: Item) => {
    if (!byStay) return kindIndex.get(kindOf(item)) ?? elsewhere;
    let group = elsewhere;
    let best = Number.POSITIVE_INFINITY;
    if (from)
      at.forEach((p, n) => {
        const d = distanceM(p, from);
        if (d <= NEAR_STAY_M && d < best) {
          best = d;
          group = groupOf[n] ?? elsewhere;
        }
      });
    return group;
  };

  // A choice's options: one group for all of them, by their middle (or the first's kind).
  const byChoice = new Map<string, Item[]>();
  for (const i of placed) {
    const c = optionsOf.get(i.id);
    if (c) byChoice.set(c.id, [...(byChoice.get(c.id) ?? []), i]);
  }
  const choiceGroup = new Map<string, number>();
  // The stay a choice already has chosen or booked, by the choice: its place in `stays`.
  const pickedStay = new Map(stays.flatMap((s, n) => (s.parent ? [[s.parent, n] as const] : [])));
  for (const [id, options] of byChoice) {
    const picked = pickedStay.get(id);
    if (picked !== undefined) {
      choiceGroup.set(id, groupOf[picked] ?? elsewhere);
      continue;
    }
    const ordered = [...options].sort(byOrder);
    const box = bounds(ordered.map((o) => point(o) as LngLat));
    const middle = box ? midway(box[0], box[1]) : null;
    choiceGroup.set(id, groupAt(middle, ordered[0] as Item));
  }

  // Ranks within a group: its stays in the order we stay there, each with the open options of its choice
  // right after it (its alternatives); the open options of choices with none picked; the other stays,
  // places by kind, places that are a station or an airport, journeys (by kind: options first, then the
  // rest, then journeys). `sub` orders the stays and their options.
  type Entry = { item: Item; group: number; rank: number; sub: number; choice?: Item };
  const entries: Entry[] = placed.map((item) => {
    const choice = optionsOf.get(item.id);
    if (choice) {
      const picked = pickedStay.get(choice.id);
      const group = choiceGroup.get(choice.id) ?? 0;
      return picked === undefined
        ? { item, group, rank: -0.5, sub: 0, choice }
        : { item, group, rank: -1, sub: 2 * picked + 1, choice };
    }
    const anchor = own.get(item.id);
    if (anchor !== undefined) return { item, group: groupOf[anchor] ?? elsewhere, rank: -1, sub: 2 * anchor };
    if (!byStay) return { item, group: groupAt(null, item), rank: item.type === "move" ? 1 : 0, sub: 0 };
    const rank = item.type === "move" ? PLACE_ORDER.length : PLACE_ORDER.indexOf(kindOf(item));
    return { item, group: groupAt(leaves(item), item), rank, sub: 0 };
  });
  const order = entries.sort(
    (a, b) =>
      a.group - b.group ||
      a.rank - b.rank ||
      a.sub - b.sub ||
      (a.choice && b.choice
        ? firstDay(a.choice, start) - firstDay(b.choice, start) ||
          collator.compare(a.choice.title, b.choice.title) ||
          a.choice.id.localeCompare(b.choice.id) ||
          byOrder(a.item, b.item)
        : 0) ||
      // Journeys by when they leave, then by name.
      (a.item.type === "move" && b.item.type === "move"
        ? firstDay(a.item, start) - firstDay(b.item, start) || startsAt(a.item).localeCompare(startsAt(b.item))
        : 0) ||
      byName(a.item, b.item),
  );
  return {
    byStay,
    heads,
    order: order.map(({ item, group, choice }) => (choice ? { item, group, choice } : { item, group })),
  };
}

// A choice's options in its own order, then by name.
const byOrder = (a: Item, b: Item) =>
  (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY) || byName(a, b);

// The groups with something shown in them, in order, each with what's shown of it: a choice's options
// shown together under its title.
export function listGroups(layout: ListLayout, shown: ReadonlySet<string>): ListGroup[] {
  const groups = layout.heads.map(() => ({ items: [] as Item[], entries: [] as ListEntry[] }));
  for (const { item, group, choice } of layout.order) {
    const g = groups[group];
    if (!g || !shown.has(item.id)) continue;
    g.items.push(item);
    const last = g.entries.at(-1);
    if (choice && last && "choice" in last && last.choice.id === choice.id) last.options.push(item);
    else g.entries.push(choice ? { choice, options: [item] } : { item });
  }
  return layout.heads.flatMap((head, n) => {
    const g = groups[n];
    return g && g.items.length > 0 ? [{ ...head, ...g }] : [];
  });
}
